import { RoleReducer } from '@leverege/api-redux';
import { GlobalState } from '@leverege/ui-redux';
import Molten from "../Molten.js";
import Util from "./Util.js";
export default class UserRoles {
  /**
   * Creates an object of { <resourceId> : { isAdmin : true|false, roles : [ role1, role2 ], customRoles : [ role2 ] } } for 
   * quick lookup. Admin will be determine by 1) a role of projectId === imagine and name === 'Admin', 
   * or by metadata.isAdmin === true
   */
  static extract = Util.extract;
  static isUsedAtBlueprint(role, blueprint, {
    usedAtKey = 'usedAtBlueprints',
    nullIsTrue = true
  } = {}) {
    const usedAtBlueprints = role.metadata?.[usedAtKey];
    if (usedAtBlueprints != null) {
      if (Array.isArray(usedAtBlueprints)) {
        return usedAtBlueprints.includes(blueprint.type);
      }
      if (typeof usedAtBlueprints === 'string') {
        return usedAtBlueprints === '*';
      }
      return false;
    }
    return nullIsTrue;
  }
  static isUsedAtUi(role, molten, {
    usedAtKey = 'usedAtUis',
    nullIsTrue = true
  } = {}) {
    let nMolten = molten;
    if (!molten) {
      nMolten = Molten.global();
    }
    const usedAtUis = role.metadata?.[usedAtKey];
    const uiName = nMolten?.getConfig('uiName') || 'default';
    if (usedAtUis != null) {
      if (Array.isArray(usedAtUis)) {
        return usedAtUis.includes(uiName);
      }
      if (typeof usedAtUis === 'string') {
        return usedAtUis === '*';
      }
      return false;
    }
    return nullIsTrue;
  }
  static isUsedAtSystem(role) {
    const usedAtBlueprints = role.metadata?.usedAtSystem;
    return usedAtBlueprints === true;
  }
  static canAssign(model, role, resources) {
    const state = GlobalState.get();
    const neededPermissions = role?.permissions?.reduce((needed, permission) => {
      // fine to modify for efficiency here
      // eslint-disable-next-line no-param-reassign
      needed[permission.id] = true;
      return needed;
    }, {});
    for (let n = 0; n < resources.length; n++) {
      const r = resources[n];
      if (model[r] == null) {
        continue; // no match
      }
      // fast checks
      if (model[r].isAdmin || model[r].roleIds[role.id]) {
        return true;
      }
      // check permission-by-permission
      model[r].roles.forEach(roleHeader => {
        const r = RoleReducer.getRolesRef(state.roles, roleHeader.projectId)?.[roleHeader.id];
        r?.permissions?.forEach(p => {
          if (p?.id) {
            delete neededPermissions[p.id];
          }
        });
      });
      if (Object.keys(neededPermissions).length === 0) {
        return true;
      }
    }
    return false;
  }
  static createBlockedFromPredicate(molten) {
    const uiName = molten.getConfig('uiName') || 'default';
    return role => {
      const {
        scope,
        name
      } = role;
      const blockedUIs = role?.metadata?.ui?.blockFrom;
      if (scope === 'platform' && name === 'Admin') {
        return false;
      }
      if (scope === 'platform' && name === 'User') {
        return true;
      }
      if (Array.isArray(blockedUIs)) {
        return blockedUIs.includes(uiName);
      }
      return false;
    };
  }
  static createUIRoleFilter(molten) {
    const uiName = molten.getConfig('uiName');
    return role => {
      const isCustom = role.scope === 'custom';
      const excludeFrom = role.metadata?.ui?.excludeFrom;
      let exclude = false;
      if (Array.isArray(excludeFrom)) {
        exclude = excludeFrom.includes('*') || uiName && excludeFrom.includes(uiName) || uiName == null && excludeFrom.includes('default');
      } else {
        exclude = excludeFrom === '*' || uiName && excludeFrom === uiName || uiName == null && excludeFrom === 'default';
      }
      return isCustom && !exclude;
    };
  }
  static createRoleFilter(model, resources, blueprint, molten) {
    return role => {
      return UserRoles.isUsedAtBlueprint(role, blueprint) && UserRoles.isUsedAtUi(role, molten) && UserRoles.canAssign(model, role, resources);
    };
  }
  static createTempUserRoleFilter(model, resources, blueprint) {
    return role => {
      return UserRoles.isUsedAtBlueprint(role, blueprint, {
        usedAtKey: 'usedByTempUsersAtBlueprints',
        nullIsTrue: false
      }) && UserRoles.canAssign(model, role, resources);
    };
  }
  static createSystemRoleFilter(model, resources, molten) {
    return role => UserRoles.isUsedAtSystem(role) && UserRoles.isUsedAtUi(role, molten) && UserRoles.canAssign(model, role, resources);
  }
}